export type FootballSelectionSortable = {
    name: string;
    templateId?: string;
    templateMarketId?: string | void;
    identifier?: string;
};

const WINNING_MARGIN = /winning-?margin/i;
const BANDS = /-bands/i; // to cover all bands markets
const EXACT_NUMBER_OF_GOALS = /exact-number-of-goals/i;
const WHICH_TEAM_TO_SCORE = /which-teams?-to-score/i;
const MATCH_RESULT_AND_GOALS = /match-result-and-goals-over-under/i;
const FIRST_GOAL_TIME = /first-goal-time/i;

export const isFootballBusinessOrderMarket = (marketTemplateId: string | undefined | void): boolean => {
    if (marketTemplateId === undefined || marketTemplateId === '') {
        return false;
    }

    return (
        WINNING_MARGIN.test(marketTemplateId) ||
        BANDS.test(marketTemplateId) ||
        EXACT_NUMBER_OF_GOALS.test(marketTemplateId) ||
        WHICH_TEAM_TO_SCORE.test(marketTemplateId) ||
        MATCH_RESULT_AND_GOALS.test(marketTemplateId) ||
        FIRST_GOAL_TIME.test(marketTemplateId)
    );
};

const getSelectionTemplateId = (selection: FootballSelectionSortable): string => {
    return (selection.identifier ?? selection.templateId ?? '').trim();
};

const parseWinningMarginWeight = (templateId: string): number => {
    const id = templateId.toUpperCase();

    if (id === 'D') {
        return 200;
    }
    if (id === 'NG') {
        return 400;
    }

    const match = id.match(/^([HA])(\d+)/);
    if (match === null) {
        return 9999;
    }

    const side = match[1];
    const margin = parseInt(match[2] ?? '0', 10);
    const sideBase = side === 'H' ? 100 : 300;

    return sideBase + margin;
};

const parseBandWeight = (templateId: string): number => {
    const id = templateId.toUpperCase();
    if (id === 'NG' || id.includes('NO GOAL') || id.includes('NOGOAL')) {
        return 9998;
    }

    const match = templateId.match(/^(\d+)/);
    if (match === null) {
        return 9999;
    }

    return parseInt(match[1] ?? '0', 10);
};

const parseExactGoalsWeight = (templateId: string): number => {
    const match = templateId.match(/^(\d+)/);
    if (match === null) {
        return 9999;
    }

    return parseInt(match[1] ?? '0', 10);
};

const parseWhichTeamToScoreWeight = (selection: FootballSelectionSortable): number => {
    const templateId = getSelectionTemplateId(selection).toUpperCase();
    if (templateId === 'H') {
        return 1;
    }
    if (templateId === 'A') {
        return 2;
    }

    const name = selection.name.toLowerCase();
    if (name.includes('neither')) {
        return 3;
    }
    if (name.includes('both')) {
        return 4;
    }

    return 9999;
};

const RESULT_ORDER: Record<string, number> = { H: 1, D: 2, A: 3 };
const LINE_ORDER: Record<string, number> = { U: 1, O: 2 };

const parseMatchResultAndGoalsWeight = (selection: FootballSelectionSortable): number => {
    const templateId = getSelectionTemplateId(selection).toUpperCase();
    const templateMatch = templateId.match(/^([HDA])[-_/]?([UO])/);
    if (templateMatch !== null) {
        const result = templateMatch[1] ?? '';
        const line = templateMatch[2] ?? '';
        return (RESULT_ORDER[result] ?? 9) * 10 + (LINE_ORDER[line] ?? 9);
    }

    const name = selection.name.toLowerCase();
    const line = name.includes('under') ? 'U' : name.includes('over') ? 'O' : '';
    const result = name.includes('draw')
        ? 'D'
        : templateId.startsWith('H')
          ? 'H'
          : templateId.startsWith('A')
            ? 'A'
            : templateId.startsWith('D')
              ? 'D'
              : '';

    if (result === '' || line === '') {
        return 9999;
    }

    return (RESULT_ORDER[result] ?? 9) * 10 + (LINE_ORDER[line] ?? 9);
};

export const getFootballSelectionSortWeight = (selection: FootballSelectionSortable): number => {
    const marketTemplateId = selection.templateMarketId;
    if (marketTemplateId === undefined || marketTemplateId === '') {
        return 9999;
    }

    const templateId = getSelectionTemplateId(selection);

    if (WINNING_MARGIN.test(marketTemplateId)) {
        return parseWinningMarginWeight(templateId);
    }
    if (BANDS.test(marketTemplateId) || FIRST_GOAL_TIME.test(marketTemplateId)) {
        return parseBandWeight(templateId);
    }
    if (EXACT_NUMBER_OF_GOALS.test(marketTemplateId)) {
        return parseExactGoalsWeight(templateId);
    }
    if (WHICH_TEAM_TO_SCORE.test(marketTemplateId)) {
        return parseWhichTeamToScoreWeight(selection);
    }
    if (MATCH_RESULT_AND_GOALS.test(marketTemplateId)) {
        return parseMatchResultAndGoalsWeight(selection);
    }

    return 9999;
};

export const sortFootballMarketSelections = <T extends FootballSelectionSortable>(selections: Array<T>): Array<T> => {
    if (selections.length === 0) {
        return selections;
    }

    const marketTemplateId = selections[0]?.templateMarketId;
    if (!isFootballBusinessOrderMarket(marketTemplateId)) {
        return selections;
    }

    return [...selections].sort((prev, next) => {
        const weightDiff = getFootballSelectionSortWeight(prev) - getFootballSelectionSortWeight(next);
        if (weightDiff !== 0) {
            return weightDiff;
        }

        return prev.name.localeCompare(next.name);
    });
};
